import { useCallback, useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AutoComplete from 'components/field/AutoComplete';
import StringInput from 'components/field/StringInput';
import Table from 'components/table/Table';

import { AUTOCOMPLETE } from 'services/consts';

const SalesChannelAutocomplete = ({ name, value, onChange, ...rest }) => {
	// Statics
	const salesChannelsAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const salesChannelsAutocompleteLabels = useMemo(() => ['Name'], []);

	return (
		<AutoComplete
			name={name}
			autoCompleteName={AUTOCOMPLETE.SALES_CHANNELS}
			columns={salesChannelsAutocompleteColumns}
			selectionLabelAccessor={salesChannelsAutocompleteLabels}
			value={value}
			setValue={(value) => onChange(value)}
			{...rest}
		/>
	);
};

export const StoreItems = ({ formMethods, formMode, isLoading }) => {
	const { t } = useTranslation();

	// Form methods passed from inbound form
	const { control, setValue, watch } = formMethods;

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'stores'
	});

	// Methods
	const removeItem = useCallback((index) => {
		remove(index);
	}, []);

	const onSalesChannelChange = useCallback((value, index) => {
		setValue(`stores[${index}].salesChannelCredentialsID`, value?.ID, {
			shouldValidate: true,
			shouldDirty: true
		});
	}, []);

	const columns = useMemo(
		() => [
			{
				header: t('storeCredentialName'),
				accessor: 'storeName',
				width: 200,
				renderCell: ({ row }) => (
					<StringInput
						name={`stores[${row.index}].storeName`}
						value={watch(`stores[${row.index}].storeName`)}
						disabled={formMode !== 'add'}
					/>
				),
				editCell: ({ row }) => (
					<StringInput
						name={`stores[${row.index}].storeName`}
						value={watch(`stores[${row.index}].storeName`)}
					/>
				)
			},
			{
				header: t('salesChannel'),
				accessor: 'salesChannelCredentialsID',
				width: 200,
				renderCell: ({ row }) => (
					<SalesChannelAutocomplete
						name={`stores[${row.index}].salesChannelCredentialsID`}
						value={watch(`stores[${row.index}].salesChannelCredentialsID`)}
						onChange={(value) => onSalesChannelChange(value, row.index)}
						disabled={formMode !== 'add'}
					/>
				),
				editCell: ({ row }) => (
					<SalesChannelAutocomplete
						name={`stores[${row.index}].salesChannelCredentialsID`}
						value={watch(`stores[${row.index}].salesChannelCredentialsID`)}
						onChange={(value) => onSalesChannelChange(value, row.index)}
					/>
				)
			},
			...(formMode === 'edit'
				? [
						{
							id: 'editRow',
							size: 30,
							isVisible: (row) => watch(`stores[${row.index}].salesChannelCredentialsID`) !== undefined,
							maxSize: 30,
							showOnMobile: true
						}
					]
				: []),
			...(formMode === 'edit' || formMode === 'add' || !formMode
				? [
						{
							id: 'deleteRow',
							size: 30,
							maxSize: 30,
							showOnMobile: true
						}
					]
				: [])
		],
		[]
	);

	return (
		<Table
			loading={isLoading}
			data={fields}
			columns={columns}
			onRowDelete={(row) => {
				removeItem(row.index);
			}}
			onRowAdd={() =>
				append({
					storeName: null,
					salesChannelCredentialsID: null
				})
			}
			showAddNewRowButton={formMode !== 'view'}
			deletableLastRow
		/>
	);
};
